import React, { useCallback } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import Image from '@atlaskit/image/image';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import EmptySuggestionsDark from './assets/EmptySuggestionsDark.svg';
import EmptySuggestionsDoneDark from './assets/EmptySuggestionsDoneDark.svg';
import EmptySuggestionsDoneLight from './assets/EmptySuggestionsDoneLight.svg';
import EmptySuggestionsLight from './assets/EmptySuggestionsLight.svg';
import {
	SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
	useSuggestionsEmptyStateExposureAnalytics,
} from '../analytics.ts';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		height: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
});
const i18n = defineMessages({
	emptyHeading: {
		id: 'suggestions-panel.empty-state.heading',
		defaultMessage: 'Get suggestions to improve your content.',
		description: 'Heading shown in the suggestions panel when there are no suggestions',
	},
	noSuggestionsResultHeading: {
		id: 'suggestions-panel.empty-state.no-suggestions-result.heading',
		defaultMessage: 'No suggestions yet. Before you share, review your content again.',
		description:
			'Heading shown in the suggestions panel when Rovo finishes reviewing content but does not find any suggestions',
	},
});

type SuggestionsEmptyStateVariant = 'initial' | 'noSuggestionsResult';

type SuggestionsEmptyStateProps = {
	api?: FullPageEditorPresetAPI;
	shouldFireExposureAnalytics?: boolean;
	variant?: SuggestionsEmptyStateVariant;
};

export const SuggestionsEmptyState = ({
	api,
	shouldFireExposureAnalytics = true,
	variant = 'initial',
}: SuggestionsEmptyStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	useSuggestionsEmptyStateExposureAnalytics({
		enabled: shouldFireExposureAnalytics,
		entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
	});
	const handleClick = useCallback(() => {
		api?.aiSuggestions?.actions?.setThinkingState('thinking', {
			entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
		});
	}, [api]);

	const headingMessage =
		variant === 'noSuggestionsResult' ? i18n.noSuggestionsResultHeading : i18n.emptyHeading;
	const imageSource =
		variant === 'noSuggestionsResult' ? EmptySuggestionsDoneLight : EmptySuggestionsLight;
	const darkImageSource =
		variant === 'noSuggestionsResult' ? EmptySuggestionsDoneDark : EmptySuggestionsDark;

	return (
		<div css={styles.container}>
			<Image src={imageSource} srcDark={darkImageSource} width={64} alt="" />
			<Text size="medium" align="center">
				{formatMessage(headingMessage)}
			</Text>
			<Button iconBefore={TextReviewIcon} onClick={handleClick}>
				{formatMessage(aiSuggestionsMessages.suggestEditsButtonLabel)}
			</Button>
		</div>
	);
};
